import { h, Component } from 'preact';

import { InquiryDateIndicator } from '../shared/inquiry-date-indicator';
import { InquiryDetailsIcon } from '../shared/inquiry-details-icon';
import { InquiryDetailsPropertyPreferences, InquiryAmenity, InquiryFTF, InquiryRangeValue, Inquiry } from 'inquiries/inquiry.model';
import { ReqState } from '../../../../../types';
import { InquiryLoadingMessage, InquiryLoadFailureMessage } from './loaders';

interface InquiryDetailsFTFProps {
  inquiryFTF: InquiryFTF;
  ftfReqState: ReqState;
  inquiry: Inquiry;

  onEditClick: () => void;
}

interface InquiryDetailsFTFState {}

export class InquiryDetailsFTF extends Component<InquiryDetailsFTFProps, InquiryDetailsFTFState> {
 
  getRangeValue = (val: InquiryRangeValue): string => {
    const str = [];

    if (val && val.min) 
      str.push(val.min.toLocaleString())

    if (val && val.max)
      str.push(val.max.toLocaleString())

    if (str.length == 2)
      str.splice(1, 0, ' - ')

    return str.join('');
  }

  getFurnishingString(fieldName: string) {
    switch(fieldName) {
      case 'furnishingFF': return 'Fully Furnished'
      case 'furnishingSF': return 'Semi-Furnished'
      case 'furnishingUF': return 'Unfurnished'
    }
  }

  getDesignationString(designationCode: number) {
    switch (designationCode) {
      case 1: return 'Buyer/Renter';
      case 2: return 'Broker/Agent'
      default: return 'Other';
    }
  }

  getPersonalDetailsBlock(props:  InquiryDetailsFTFProps) {
    const contact = props.inquiryFTF.contact;
    const keys = Object.keys(contact);

    const details = keys.map(key => {
      if (key === 'designation') {
        return (
          <div className="col-md-6 hi-inqdetails__ftf__amenities-icon">
            <InquiryDetailsIcon 
              icon={ key.toLowerCase() }
              hasMargin={ true }
              label={ this.getDesignationString(contact.designation) } />
          </div>
        );
      } else {
        return (
          <div className="col-md-6 hi-inqdetails__ftf__amenities-icon">
            <InquiryDetailsIcon 
              icon={ key.toLowerCase() }
              hasMargin={ true }
              label={ contact[key] } />
          </div>
        );
      }
    });

    return (
      <div className="row">
        { details }
      </div>
    )
  } 

  getPropertyPreferenceBlock({ inquiryFTF }: InquiryDetailsFTFProps) {
    let location: string = ""
    const bedrooms = [];
    const furnish = [];

    const prefs: InquiryDetailsPropertyPreferences = {
      furnish: '',
      type: inquiryFTF.type,
      floorArea: this.getRangeValue(inquiryFTF.floorArea),
      budget: `PHP ${this.getRangeValue(inquiryFTF.budget)}`,
      location,
    };

    if (
      inquiryFTF.locations && 
      inquiryFTF.locations.locations.length > 0
    ) {
      location = inquiryFTF.locations.locations[0].city
    }

    for(let item in inquiryFTF) {
      if (item.startsWith('bedroom')) {
        bedrooms.push({ field: item, value: inquiryFTF[item] })
      }

      if (item.startsWith('furnishing')) {
        furnish.push({ field: item, value: inquiryFTF[item] })
      }
    }

    bedrooms.forEach(br => br.value !== 0 ? 
      prefs.bedrooms = br.value + ( br.value == 1 ? ' Bedroom' : ' Bedrooms' ) : '')

    furnish.forEach(fr => fr.value !== 0 ? 
      prefs.furnish += this.getFurnishingString(fr.field) + ' ' : '')

    return (
      <div className="row">
        { Object.keys(prefs).map(key => prefs[key] ?
            <div className="col-md-6 hi-inqdetails__ftf__prefs-icon">
              <InquiryDetailsIcon
                icon={ key.toLowerCase() }
                hasMargin={ true }
              label={ prefs[key].toString() } />
            </div> : null )}
      </div>
    )
  }

  getAmenitiesBlock({ inquiryFTF }: InquiryDetailsFTFProps) {
    const amenities: InquiryAmenity[] = [];
    const amenityList = ['balcony/terrace', 'swimming pool', 'garden', 'gym', 'pet friendly', 'parking spaces'];

    if (inquiryFTF.amenities && inquiryFTF.amenities.length > 0 ) {
      inquiryFTF.amenities.forEach(am => {
        if (amenityList.includes(am.field.toLowerCase()))
          amenities.push(am);
      })
    }

    return (
      <div className="row">
        { amenities.map(item => item.value ? 
            <div className="col-md-6 hi-inqdetails__ftf__amenities-icon">
            <InquiryDetailsIcon 
              icon={ item.field.toLowerCase() }
              hasMargin={ true }
              label={ item.field } />
          </div> 
        : null )}
      </div>
    )
  }

  getPaymentTermsBlock({ inquiryFTF }: InquiryDetailsFTFProps) {
    const payment = {
      paymentTerm: inquiryFTF.paymentTerm ? 'Cash or PDCs' : '',
      receipts: inquiryFTF.receipts ? 'With Receipts' : '',
      companyName: inquiryFTF.companyName ? inquiryFTF.companyName : ''
    };
    
    return (
      <div className="row">
      { Object.keys(payment).map(key => payment[key] ? 
          <div className="col-md-6 hi-inqdetails__ftf__payment-icon">
            <InquiryDetailsIcon
              icon={ key.toLowerCase() }
              hasMargin={ true }
              label={ payment[key] } />
          </div> : null )}
    </div>
    )

  }

  render(props: InquiryDetailsFTFProps, state: InquiryDetailsFTFState) {
    if (props.ftfReqState === ReqState.IN_PROGRESS) {
      return <InquiryLoadingMessage message='Loading FTF details...' />
    } else if (props.ftfReqState === ReqState.FAILED) {
      return (
        <InquiryLoadFailureMessage message='Could not load FTF details' />
      );
    }

    const { dateMoveIn, dateViewing } = props.inquiryFTF;

    return (
      <section className="hi-inqdetails__ftf">    
        <div className="hi-inqdetails__ftf__dates">
          <div className="text-center">
            <button className="btn btn-link hi-inqdetails__ftf-edit-button" onClick={ props.onEditClick }>
              Edit FTF <i className="far fa-edit" />
            </button>
          </div>
          <div className="row">
            <div className="col-md-6 d-flex mt-3 align-items-center">
              <InquiryDateIndicator 
                highlighted={ false }
                date={ dateViewing } />
              <p className="mb-0 ml-3">Preferred Viewing Date</p>
            </div>

            <div className="col-md-6 d-flex mt-3 align-items-center">
              <InquiryDateIndicator 
                highlighted={ true }
                date={ dateMoveIn } />
              <p className="mb-0 ml-3">Target Move-in Date</p>
            </div>
          </div>
        </div>

        <div className="hi-inqdetails__ftf__personal">
          <p className="hi-inqdetails__ftf__personal-title">Source</p>
          <div className="row">
            <div className="col-md-6 hi-inqdetails__ftf__amenities-icon">
              <InquiryDetailsIcon icon='fa-mobile-alt'
                hasMargin={ true }
                label={ props.inquiry.source } />
            </div>
          </div>
        </div>

        <div className="hi-inqdetails__ftf__personal">
          <p className="hi-inqdetails__ftf__personal-title">Personal</p>
          { this.getPersonalDetailsBlock(props) }
        </div>

        <div className="hi-inqdetails__ftf__prefs">
          <p className="hi-inqdetails__ftf__prefs-title">Property Preferences</p>
          { this.getPropertyPreferenceBlock(props) }
        </div>

        <div className="hi-inqdetails__ftf__amenities">
          <p className="hi-inqdetails__ftf__amenities-title">Amenities</p>
          { this.getAmenitiesBlock(props) }
        </div>

        <div className="hi-inqdetails__ftf__payment">
          <p className="hi-inqdetails__ftf__payment-title">Payment Terms</p>
          { this.getPaymentTermsBlock(props) }
        </div>

      </section>
    )
  }
}
